import * as React from "react"
import { Check, ChevronDown, Loader2, Search } from "lucide-react"

import { cn } from "../../lib/utils"

// Options and loading come from the caller; nothing is filtered locally, so TRN matches survive.
const Combobox = React.forwardRef(function Combobox(
  {
    options = [],
    value = null,
    onChange,
    onSearchChange,
    getOptionLabel = (o) => (o == null ? "" : String(o)),
    getOptionKey = (o) => o?.id,
    loading = false,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyText = "No results found",
    loadingText = "Searching...",
    disabled = false,
    className,
    contentClassName,
    triggerClassName,
  },
  ref
) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const containerRef = React.useRef(null)
  const inputRef = React.useRef(null)

  React.useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [open])

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus()
  }, [open])

  const updateSearch = (next) => {
    setSearch(next)
    if (onSearchChange) onSearchChange(next)
  }

  // Closing clears the search so reopening never shows stale matches.
  const closeAndReset = () => {
    setOpen(false)
    if (search) updateSearch("")
  }

  const handleSelect = (option) => {
    if (onChange) onChange(option)
    closeAndReset()
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={() => (open ? closeAndReset() : setOpen(true))}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          !value && "text-muted-foreground",
          triggerClassName
        )}
      >
        <span className="line-clamp-1 text-left">{value ? getOptionLabel(value) : placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
            contentClassName
          )}
        >
          <div className="relative border-b">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={search}
              onChange={(event) => updateSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault()
                  closeAndReset()
                }
              }}
              placeholder={searchPlaceholder}
              className="h-9 w-full bg-transparent py-2 pl-8 pr-2 text-sm outline-none placeholder:text-muted-foreground"
            />
            {loading && <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin opacity-60" />}
          </div>

          <div className="max-h-60 overflow-y-auto p-1">
            {loading ? (
              <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingText}
              </div>
            ) : options.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">{emptyText}</div>
            ) : (
              options.map((option) => {
                const selected = value && getOptionKey(option) === getOptionKey(value)
                return (
                  <button
                    key={getOptionKey(option)}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={cn(
                      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                      selected && "bg-accent/50"
                    )}
                  >
                    {selected && (
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                    {getOptionLabel(option)}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
})

export { Combobox }
